import { motion, smoothTransition, prefersReducedMotion } from "@/lib/motion";
import { ArrowRight, Sparkles, Zap, Database, Bot, Cog, Users, Monitor } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { LogoCarousel } from "@/components/LogoCarousel";
import { FloatingOrbs } from "@/components/FloatingOrbs";
import { TypewriterText } from "@/components/TypewriterText";

export const Hero = () => {
  // Skip heavy animations if user prefers reduced motion
  const skipAnimations = prefersReducedMotion;
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Floating animated orbs */}
      <FloatingOrbs />
      
      {/* Animated grid pattern */}
      <motion.div 
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage: `linear-gradient(hsl(var(--primary) / 0.5) 1px, transparent 1px),
                            linear-gradient(90deg, hsl(var(--primary) / 0.5) 1px, transparent 1px)`,
          backgroundSize: '80px 80px'
        }}
        animate={{
          backgroundPosition: ["0px 0px", "80px 80px"],
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          ease: "linear",
        }}
      />
      
      {/* Radial gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-background/90 to-background pointer-events-none" />
      
      {/* Spotlight effect */}
      <motion.div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] opacity-60"
        style={{
          background: "conic-gradient(from 180deg at 50% 50%, transparent 0deg, hsl(var(--primary) / 0.1) 60deg, transparent 120deg)",
        }}
        animate={{
          rotate: [0, 360],
        }}
        transition={{
          duration: 30,
          repeat: Infinity,
          ease: "linear",
        }}
      />

      <div className="container mx-auto px-6 relative z-10 pt-24">
        <div className="max-w-5xl mx-auto text-center">
          {/* Animated Badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full glass mb-10 group cursor-default"
          >
            <motion.div
              animate={{ rotate: [0, 15, -15, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            >
              <Sparkles className="w-4 h-4 text-primary" />
            </motion.div>
            <span className="text-sm font-medium text-foreground/80">Data-Driven Growth Partner</span>
            <Zap className="w-3 h-3 text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
          </motion.div>

          {/* Main Heading with Typewriter */}
          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold leading-[1.1] mb-8 tracking-tight"
          >
            <TypewriterText />
            <span className="text-foreground">, </span>
            <br className="hidden sm:block" />
            <motion.span 
              className="text-foreground inline-block"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              & Consultancy
            </motion.span>
          </motion.h1>

          {/* Tagline with stagger animation */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="overflow-hidden mb-8"
          >
            <motion.p
              initial={{ y: 60 }}
              animate={{ y: 0 }}
              transition={{ duration: 0.8, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold"
            >
              <span className="text-muted-foreground">Without the </span>
              <motion.span 
                className="text-primary relative inline-block"
                whileHover={{ scale: 1.05 }}
                transition={{ type: "spring", stiffness: 400 }}
              >
                bullshit
                <motion.span
                  className="absolute -bottom-1 left-0 w-full h-[3px] bg-primary rounded-full"
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ duration: 0.8, delay: 1 }}
                />
              </motion.span>
              <span className="text-muted-foreground">.</span>
            </motion.p>
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-6 leading-relaxed"
          >
            I don't hide behind fancy words or overinflated claims. What you see is what you get: 
            someone who's honest, direct, and fully committed to delivering real results.
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.15 }}
            className="text-muted-foreground max-w-2xl mx-auto mb-12 leading-relaxed"
          >
            Solved Media is a data-driven growth partner helping businesses generate leads, automate conversations and build systems that actually work. From consumer data to AI, automations and high-performing websites, everything we do is focused on measurable results, not vanity metrics.
          </motion.p>

          {/* CTA Buttons with enhanced styling */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <Link to="/contact">
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Button 
                  size="lg" 
                  className="relative bg-primary text-primary-foreground hover:bg-primary/90 text-lg px-10 py-7 group overflow-hidden"
                >
                  <motion.span
                    className="absolute inset-0 bg-gradient-to-r from-primary via-primary/80 to-primary"
                    animate={{
                      x: ["-100%", "100%"],
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: "linear",
                    }}
                    style={{ opacity: 0.3 }}
                  />
                  <span className="relative flex items-center">
                    Get Started
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </span>
                </Button>
              </motion.div>
            </Link>
            
            <Link to="/about">
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-border/50 text-foreground hover:bg-secondary/30 hover:border-primary/50 text-lg px-10 py-7 backdrop-blur-sm"
                >
                  Learn More About Our Services
                </Button>
              </motion.div>
            </Link>
          </motion.div>

          {/* Services highlight */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-20 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 max-w-4xl mx-auto"
          >
            {[
              { icon: Database, label: "Consumer Data", link: "/data" },
              
              { icon: Bot, label: "AI Chatbots", link: "/ai" },
              { icon: Cog, label: "Automations", link: "/automations" },
              { icon: Monitor, label: "Websites", link: "/website-build" },
              { icon: Users, label: "Consultancy", link: "/consultancy" },
            ].map((service, index) => (
              <Link key={service.label} to={service.link}>
                <motion.div
                  className="glass p-4 rounded-xl text-center group cursor-pointer hover:border-primary/50 transition-colors"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 1 + index * 0.1 }}
                  whileHover={{ scale: 1.05, y: -5 }}
                >
                  <service.icon className="w-8 h-8 mx-auto mb-2 text-primary group-hover:scale-110 transition-transform" />
                  <div className="text-sm font-medium text-foreground/80 group-hover:text-foreground transition-colors">{service.label}</div>
                </motion.div>
              </Link>
            ))}
          </motion.div>

          {/* Stats row */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.1 }}
            className="mt-12 grid grid-cols-3 gap-8 max-w-2xl mx-auto"
          >
            {[
              { value: "100+", label: "Clients Served" },
              { value: "5M+", label: "Leads Generated" },
              { value: "99%", label: "Client Satisfaction" },
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 1.2 + index * 0.1 }}
              >
                <motion.div 
                  className="text-3xl md:text-4xl font-bold text-gradient mb-1"
                  whileHover={{ scale: 1.1 }}
                  transition={{ type: "spring", stiffness: 400 }}
                >
                  {stat.value}
                </motion.div>
                <div className="text-sm text-muted-foreground">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>

          {/* Trust indicators */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.2 }}
            className="mt-20 pt-16 border-t border-border/30"
          >
            <LogoCarousel />
          </motion.div>
        </div>
      </div>
    </section>
  );
};
